/**
 * MainLayout wrapper with chat sidebar state management (Simplified)
 * 범용적인 MainLayout을 기반으로 채팅 기능을 추가 (300줄 이하 버전)
 */
import { isLearnSurface } from '@edusense/shared-constants';
import React, { useState, useEffect } from 'react';
import { brand_name } from '@paraglide/messages';
import { MainLayout, SidebarRenderer as UILayoutSidebarRenderer, MobileNavigationRenderer as UILayoutMobileNav, WriteButton } from '@edusense/shared-ui-layout';
import { ChatModal, ChatProvider } from '@edusense/ai-chat';
import { PeopleChatModal } from '@edusense/people-chat';
import { useParams, useLocation } from 'react-router';
import { useI18nNavigate } from '../hooks/useI18nNavigate';
import { handleSharedGeminiLogin } from '../../utils/shared-login';
import { UserProfilePreview } from '@edusense/entities-user';
import { useUILayoutTheme } from '../providers/ThemeProviderImpl';
import { useUILayoutAuth } from '../providers/AuthProviderImpl';
import { useUILayoutNavigation } from '../providers/NavigationProviderImpl';
import { MobileChatOverlay } from '../components/MobileChatOverlay';
import { useTabletViewMode } from '@edusense/shared-hooks';
import { LAYOUT_BREAKPOINTS } from '@edusense/shared-constants';
import { ComposeModal } from '@edusense/features-tweet-composer';

interface MainLayoutWithChatProps {
  children?: React.ReactNode;
  hideNavigation?: boolean;
  showRightSidebar?: boolean;
  showChatSidebar?: boolean;
  isVSCodeEnv?: boolean;
  activeMenu?: string;
  className?: string;
}

export const MainLayoutWithChat: React.FC<MainLayoutWithChatProps> = ({
  children,
  ...props
}) => {
  const params = useParams();
  const navigate = useI18nNavigate();
  const location = useLocation();
  const currentLang = params.lang || 'ko';
  
  const CHAT_TYPE_STORAGE_KEY = 'edusense.chat.sidebarType';
  const [isChatSidebarMode, setIsChatSidebarMode] = useState(false);
  const [isMobileView, setIsMobileView] = useState(false);
  const [chatType, setChatType] = useState<'ai' | 'people'>(() => {
    if (typeof window === 'undefined') {
      return 'ai';
    }
    const stored = window.localStorage.getItem(CHAT_TYPE_STORAGE_KEY);
    return stored === 'people' ? 'people' : 'ai';
  });
  const [isComposeOpen, setIsComposeOpen] = useState(false);

  // Provider hooks
  const themeProvider = useUILayoutTheme();
  const authProvider = useUILayoutAuth();
  const navigationItems = useUILayoutNavigation();
  const { viewMode } = useTabletViewMode();

  // Navigation handlers
  const navigateToPath = (path: string) => {
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    navigate(cleanPath);
  };

  const handleLogoClick = () => navigateToPath(isLearnSurface() ? 'learn' : 'landing');
  const handleSettingsClick = () => navigateToPath('settings');
  
  const handleMenuClick = (menuId: string) => {
    const targetItem = navigationItems.find(item => item.id === menuId);
    if (targetItem?.href) {
      const path = targetItem.href.startsWith('/') ? targetItem.href : `/${targetItem.href}`;
      navigateToPath(path);
    } else {
      navigateToPath(menuId);
    }
  };

  // 현재 활성 메뉴 찾기
  const getCurrentActiveMenu = () => {
    const activeItem = navigationItems.find(item => item.isActive);
    return activeItem?.id || 'home';
  };

  // 화면 크기 및 뷰 모드에 따른 레이아웃 상태 관리
  const [screenWidth, setScreenWidth] = useState(window.innerWidth);
  
  useEffect(() => {
    const checkScreenSize = () => {
      const width = window.innerWidth;
      setScreenWidth(width);
      setIsMobileView(width <= LAYOUT_BREAKPOINTS.MOBILE_MAX);
    };
    checkScreenSize();
    window.addEventListener('resize', checkScreenSize);
    return () => window.removeEventListener('resize', checkScreenSize);
  }, []);

  // 뷰 모드에 따른 레이아웃 결정 로직
  const getLayoutConfig = () => {
    const isTabletView = screenWidth >= LAYOUT_BREAKPOINTS.TABLET_MIN && 
                        screenWidth <= LAYOUT_BREAKPOINTS.TABLET_MAX;
    
    if (!isTabletView) {
      // 모바일이나 데스크톱에서는 기본 동작
      return { isTabletView: false, shouldShowSidebarInline: false };
    }

    // 태블릿 뷰에서 뷰 모드에 따른 레이아웃 결정
    switch (viewMode) {
      case 'sidebar-default':
        // 사이드바를 데스크톱처럼 인라인으로 표시
        return { isTabletView: true, shouldShowSidebarInline: true };
      case 'content-focused':
        // 사이드바를 모바일처럼 오버레이로 처리
        return { isTabletView: true, shouldShowSidebarInline: false };
      case 'balanced':
      default:
        // 기본 동작 (현재 로직 유지)
        return { isTabletView: true, shouldShowSidebarInline: false };
    }
  };

  const layoutConfig = getLayoutConfig();

  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }
    window.localStorage.setItem(CHAT_TYPE_STORAGE_KEY, chatType);
  }, [chatType]);

  const handleChatSidebarToggle = () => setIsChatSidebarMode(!isChatSidebarMode);
  const handleSwitchToUserChat = () => setChatType('people');
  const handleSwitchToAIChat = () => setChatType('ai');
  const handleOpenCompose = () => setIsComposeOpen(true);
  const handleCloseCompose = () => setIsComposeOpen(false);

  const buildUserProfileComponent = (isTablet: boolean) => (
    <UserProfilePreview
      user={authProvider.user!}
      onLogout={authProvider.logout}
      onSettingsClick={handleSettingsClick}
      isVSCodeEnv={props.isVSCodeEnv || false}
      isTabletView={isTablet}
    />
  );

  const desktopProfileComponent = authProvider.isAuthenticated && authProvider.user
    ? buildUserProfileComponent(layoutConfig.isTabletView)
    : undefined;

  const mobileProfileComponent = authProvider.isAuthenticated && authProvider.user
    ? buildUserProfileComponent(false)
    : undefined;

  // Global event listeners to open chat sidebar from anywhere
  useEffect(() => {
    const openPeople = () => {
      setChatType('people');
      setIsChatSidebarMode(true);
    };
    const openAI = () => {
      setChatType('ai');
      setIsChatSidebarMode(true);
    };
    window.addEventListener('open-people-chat', openPeople as EventListener);
    window.addEventListener('open-ai-chat', openAI as EventListener);
    return () => {
      window.removeEventListener('open-people-chat', openPeople as EventListener);
      window.removeEventListener('open-ai-chat', openAI as EventListener);
    };
  }, []);

  // 로그인 처리 함수
  const handleLoginClick = () => {
    try {
      handleSharedGeminiLogin(currentLang);
    } catch (error) {
      console.error('❌ handleSharedGeminiLogin error:', error);
    }
  };

  // 채팅 사이드바 렌더러
  const renderChatSidebar = () => {
    if (chatType === 'ai') {
      return (
        <ChatProvider>
          <ChatModal
            isOpen={true}
            onClose={() => {}}
            isSidebarMode={true}
            onSwitchToUserChat={handleSwitchToUserChat}
          />
        </ChatProvider>
      );
    } else {
      return (
        <PeopleChatModal
          onSwitchToAIChat={handleSwitchToAIChat}
          onClose={() => {}}
        />
      );
    }
  };

  const currentActiveMenu = getCurrentActiveMenu();

  return (
    <>
      <MainLayout
        {...props}
        injectedLocation={{
          pathname: location.pathname,
          search: location.search,
          hash: location.hash,
          key: location.key,
          state: location.state
        }}
        injectedNavigate={navigate}
        useReactRouterOutlet={true}
        themeProvider={themeProvider}
        authProvider={authProvider}
        navigationItems={navigationItems}
        showRightSidebar={props.showRightSidebar ?? true}
        showChatSidebar={(!isMobileView && isChatSidebarMode) || props.showChatSidebar}
        renderSidebar={() => {
          return (
            <UILayoutSidebarRenderer 
              navigationItems={navigationItems}
              activeMenu={props.activeMenu || currentActiveMenu}
              onMenuClick={handleMenuClick}
              logoText={brand_name()}
              logoImage="/images/favicon-edsense.ico"
              userName={authProvider.user?.name || authProvider.user?.username}
              userAvatar={authProvider.user?.profileImage || authProvider.user?.avatar}
              isAuthenticated={authProvider.isAuthenticated}
              onLogoClick={handleLogoClick}
              actionButton={
                <WriteButton
                  showWriteButton={true}
                  onWriteButtonClick={handleOpenCompose}
                  isTabletView={layoutConfig.isTabletView}
                  isMobileSidebar={isMobileView}
                />
              }
              onLogout={authProvider.logout}
              onLoginClick={handleLoginClick}
              userProfileComponent={desktopProfileComponent}
            />
          );
        }}
        renderRightSidebar={renderChatSidebar}
        renderMobileNavigation={() => (
          <UILayoutMobileNav 
            navigationItems={navigationItems}
            activeMenu={props.activeMenu || currentActiveMenu}
            onMenuClick={handleMenuClick}
            onChatSidebarToggle={handleChatSidebarToggle}
            isChatSidebarMode={isChatSidebarMode}
            logoText={brand_name()}
            logoImage="/images/favicon-edsense.ico"
            userName={authProvider.user?.name || authProvider.user?.username}
            onLogoClick={handleLogoClick}
            isAuthenticated={authProvider.isAuthenticated}
            onWriteButtonClick={handleOpenCompose}
            onLoginClick={handleLoginClick}
            onLogout={authProvider.logout}
            userProfileComponent={mobileProfileComponent}
          />
        )}
        chatOptions={{
          defaultWidth: 350,
          minWidth: 250,
          maxWidth: 500,
          enableResize: true,
          defaultType: 'ai',
        }}
      />
      
      {/* 모바일 채팅 오버레이 */}
      {isMobileView && (
        <MobileChatOverlay
          isOpen={isChatSidebarMode}
          chatType={chatType}
          onClose={handleChatSidebarToggle}
          onSwitchToAIChat={handleSwitchToAIChat}
          onSwitchToUserChat={handleSwitchToUserChat}
        />
      )}

      {/* 트윗 작성 모달 (원본 기능 파리티 버전) */}
      <ComposeModal
        isOpen={isComposeOpen}
        onClose={handleCloseCompose}
        mode="tweet"
      />
    </>
  );
};

export default MainLayoutWithChat;
